import React, { type JSX } from 'react';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useDefaultConfig, useWidgetConfigHandlers } from '../hooks';
import { HierachyChannels } from '../../WidgetConfiguration';
import { useGetAllChannelsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import Mode from '@mui/icons-material/Mode';
import Search from '@mui/icons-material/Search';
import {
    Box,
    Checkbox,
    Grid,
    IconButton,
    InputBase,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Radio,
    Stack,
    Typography,
} from '@mui/material';

import { ListSkeleton } from '../Skeletons';
import {
    setChannelSearchPayload,
    setWidgetLoader,
    setWidgetSource,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import { DataSourceModal } from 'pages/DashboardAndWidgets';
import { useDebounce } from 'hooks';
import { retrieveChannelTypefromWidgetType } from '@fiji/common/src/utils/helpers';
import { COMMAND_PALETTE } from './commandPalette';
import { CustomTransComponent } from 'components';
import { useTranslation } from 'react-i18next';

const isSettingsOrPropertiesWidget = (widgetTypeId?: string): boolean =>
    widgetTypeId === 'settings' || widgetTypeId === 'properties';

const isGaugeChartOrTrendsWidget = (widgetTypeId?: string): boolean =>
    widgetTypeId === 'gauge' || widgetTypeId === 'charts' || widgetTypeId === 'trends';

export const ChannelList = (props: any): JSX.Element => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const dataSourceModalRef = React.useRef<any>(null);
    const widgetDetails = useTypedSelector((state) => state.widgetConfiguration);
    const widgetTypeId = props?.details?.widgetType?.id;
    const isMultiDevice = Boolean(widgetDetails?.data?.multiDevice);
    const isRadioSelection = props?.selectionType === 'radio';
    const isSettingsOrProperties = isSettingsOrPropertiesWidget(widgetTypeId);
    const isGaugeChartOrTrends = isGaugeChartOrTrendsWidget(widgetTypeId);
    const selectionTitleKey =
        widgetTypeId === 'command_bar' ? 'WIDGET_MANAGEMENT:SELECT_CONTROLS' : 'REALM_MANAGEMENT:SELECT_CHANNELS';
    const searchPlaceholder = isMultiDevice ? t('COMMON:SEARCH_GROUP_AND_DEVICES') : t('COMMON:SEARCH_CHANNEL');

    const { isLoading: widgetLoader } = useWidgetConfigHandlers();
    const [, debouncedValue] = useDebounce(undefined, 2000, widgetDetails?.searchPayload);

    const {
        data: channels,
        isLoading,
        isFetching,
        isError,
    }: any = useGetAllChannelsQuery(
        {
            sourceId: widgetDetails?.source?.id ?? '',
            params: {
                channelType: retrieveChannelTypefromWidgetType(widgetTypeId),
                ...(debouncedValue?.length && {
                    searchKey: 'name',
                    searchValue: debouncedValue,
                }),
            },
            number: isGaugeChartOrTrends,
        },
        { skip: !widgetDetails?.source?.id, refetchOnMountOrArgChange: true }
    );

    React.useEffect(() => {
        if (isError) {
            dispatch(setWidgetLoader(false));
        }
    }, [isError]);

    const {
        handleChecked,
        handleChannelSelection,
        handleGaugeChannelSelection,
        isRadioSelected,
        handleChannelTypeSelection,
    } = useDefaultConfig(isSettingsOrProperties ? 'details' : widgetTypeId, channels);

    const isChannelListLoading = isLoading || isFetching || widgetLoader;

    return (
        <>
            <Grid
                px={2}
                py={3}
                className="bg-content-wrapper w-49"
                sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
                {widgetDetails?.source?.id && (
                    <Stack direction={'row'} className="flex-center-between">
                        <Typography variant="body1" fontWeight={'600'}>
                            <CustomTransComponent translationKey={'COMMON:DATA_SOURCE'} />
                        </Typography>
                        <Stack direction={'row'} alignItems={'center'}>
                            <Typography variant="body2" fontWeight={'400'}>
                                {widgetDetails?.source?.name ?? '--'}
                            </Typography>
                            <IconButton
                                onClick={(): void =>
                                    dataSourceModalRef?.current?.handleModalAction(true, widgetDetails?.source)
                                }
                            >
                                <Mode />
                            </IconButton>
                        </Stack>
                    </Stack>
                )}

                <Stack my={3}>
                    <Typography variant="body1" fontWeight={'600'}>
                        <CustomTransComponent translationKey={selectionTitleKey} />
                    </Typography>
                    <Stack mt={2} className="border-1 border-radius-4" direction={'row'}>
                        <IconButton type="button" aria-label="search" className="padding-6">
                            <Search />
                        </IconButton>
                        <InputBase
                            placeholder={searchPlaceholder}
                            inputProps={{ 'aria-label': 'search' }}
                            className="w-100"
                            value={widgetDetails?.searchPayload ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                dispatch(setChannelSearchPayload(e.target.value));
                            }}
                        />
                    </Stack>
                </Stack>

                <Box className="max-height-570 bg-white overflow-y-auto">
                    {isMultiDevice ? (
                        <HierachyChannels details={props?.details} />
                    ) : (
                        <List disablePadding>
                            {isChannelListLoading ? (
                                <ListSkeleton total={9} primaryVariant="rectangular" secondaryVariant="rectangular" />
                            ) : (
                                channels?.data?.map((item: any) => (
                                    <ListItemButton key={item?.channelId} className="border-bottom-1 list-item-hover">
                                        <ListItemText
                                            className={handleChecked(item) ? 'disabled' : ''}
                                            primary={
                                                <Typography variant="body2" fontWeight={'600'}>
                                                    {item?.displayName}
                                                </Typography>
                                            }
                                        />
                                        <ListItemIcon>
                                            {isRadioSelection ? (
                                                <Radio
                                                    edge="end"
                                                    onChange={(): void => {
                                                        handleGaugeChannelSelection({
                                                            ...item,
                                                            deviceId: widgetDetails?.device?.id,
                                                        });
                                                    }}
                                                    checked={isRadioSelected(item.channelId)}
                                                />
                                            ) : (
                                                <Checkbox
                                                    edge="end"
                                                    onChange={(): void => {
                                                        handleChannelSelection({
                                                            key:
                                                                props?.details?.widgetType?.id === 'details'
                                                                    ? handleChannelTypeSelection(item)
                                                                    : 'secondary',
                                                            channelPayload: {
                                                                ...item,
                                                                isConfirm: item.isConfirm ? item.isConfirm : true,
                                                                deviceId: '',
                                                                deviceName: widgetDetails?.device?.name,
                                                                ...(widgetTypeId === 'command_bar' && {
                                                                    paletteConfig: COMMAND_PALETTE[0],
                                                                }),
                                                            },
                                                            multiDevice: isMultiDevice,
                                                        });
                                                    }}
                                                    checked={handleChecked(item)}
                                                />
                                            )}
                                        </ListItemIcon>
                                    </ListItemButton>
                                ))
                            )}
                        </List>
                    )}
                </Box>
            </Grid>
            <DataSourceModal
                ref={dataSourceModalRef}
                type="edit"
                handleConfirm={(args: any): void => dispatch(setWidgetSource(args))}
            />
        </>
    );
};
